"use client"

import { React, useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Button } from "../ui/button"
import { ArrowLeft } from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import ObjectiveModule from "./modules/objective-module"
import BudgetModule from "./modules/budget-module"
import TimelineModule from "./modules/timeline-module"
import ExecutionPlanModule from "./modules/execution-plan-module"
import KeyUnknownsModule from "./modules/key-unknowns-module"
import MaterialsResourcesModule from "./modules/materials-resources-module"
import TaskListModule from "./modules/task-list-module"
import CollaborationModule from "./modules/collaboration-module"
import StewardshipModule from "./modules/stewardship-module"
import ActionBtcWalletStatus from "./action-btc-wallet-status"
import { useAuth0 } from "@auth0/auth0-react"
import { getApiBaseUrl } from "../../config/apiBase"
import { FEATURE_FLAGS, isFeatureEnabled } from "../../config/featureFlags"
import RefinementGuide from "../refinement/RefinementGuide"

// The existing structured modules use different JSON shapes. Keep the conversational
// guide's first release on the shared, free-text objective channel so every draft is
// readable and votable while the purpose-built editors remain available below.
const guideModules = ["objective"]

const ActionRefinementPage = () => {
  const { id: actionId } = useParams()
  const location = useLocation()
  const { getAccessTokenSilently } = useAuth0()
  const [action, setAction] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const bitcoinEnabled = isFeatureEnabled(FEATURE_FLAGS.bitcoinExperience)
  const stewardshipEnabled = isFeatureEnabled(FEATURE_FLAGS.cashCheckStewardship) || isFeatureEnabled(FEATURE_FLAGS.stripeCampaignFunding)
  const [activeTab, setActiveTab] = useState("objective")

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab")
    if (tab) {
      setActiveTab(tab)
    }
  }, [location.search])

  useEffect(() => {
    const abortController = new AbortController()

    const fetchAction = async () => {
      setLoading(true)
      setError(null)
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
        })
        const apiUrl = getApiBaseUrl()
        const response = await fetch(`${apiUrl}/api/Actions/${actionId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal,
        })
        if (response.status === 404) {
          setAction(null)
          return
        }
        if (!response.ok) {
          throw new Error(`Unable to load action (${response.status})`)
        }
        setAction(await response.json())
      } catch (requestError) {
        if (requestError.name !== "AbortError") {
          setError(requestError.message || "Unable to load action.")
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    fetchAction()
    return () => abortController.abort()
  }, [actionId, getAccessTokenSilently])

  if (loading) return <div className="flex justify-center p-8">Loading action details...</div>
  if (error) return <div className="text-red-500 p-4">Error: {error}</div>
  if (!action) return <div className="p-4">No action found with the provided ID.</div>

  // Check if action is in refinement status
  if (action.actionStatus !== "Refining") {
    return (
      <div className="container mx-auto p-4">
        <Card>
          <CardHeader>
            <CardTitle>Action: {action.actionStatement}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              This action is currently in <strong>{action.actionStatus}</strong> status and is not in the refinement
              phase.
            </p>
            <Link to={`/groups/${action.groupId}`}>
              <Button>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Group
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-4">
      <Card className="mb-4">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <Link to={`/groups/${action.groupId}`}>
              <Button variant="ghost" size="sm">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Group
              </Button>
            </Link>
            <CardTitle className="mt-2">Action Refinement: {action.actionStatement}</CardTitle>
          </div>
        </CardHeader>
      </Card>

      <RefinementGuide
        itemType="action"
        itemId={action.id}
        itemTitle={action.actionStatement || action.title}
        currentItem={action}
        modules={guideModules}
        onSuggestionPosted={() => setActiveTab("objective")}
      />

      <Card className="mb-4">
        <CardContent className="p-4">
          <strong>How refinement works</strong>
          <p className="mt-1 mb-0 text-sm text-gray-500">
            Members join the refinement, suggest concrete improvements in the area they understand, and respond to one another’s suggestions. Use the Guide above if you are unsure where your thought belongs; the modules below remain available for direct editing.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Left sidebar with module navigation */}
        <div className="md:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Refinement Modules</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="flex flex-col items-stretch h-auto w-full">
                <button
                  className={`justify-start text-left px-4 py-3 ${
                    activeTab === "objective" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"
                  }`}
                  onClick={() => setActiveTab("objective")}
                >
                  Objective
                </button>
                <button
                  className={`justify-start text-left px-4 py-3 ${
                    activeTab === "budget" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"
                  }`}
                  onClick={() => setActiveTab("budget")}
                >
                  Estimated Budget
                </button>
                <button
                  className={`justify-start text-left px-4 py-3 ${
                    activeTab === "timeline" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"
                  }`}
                  onClick={() => setActiveTab("timeline")}
                >
                  Timeline
                </button>
                <button
                  className={`justify-start text-left px-4 py-3 ${
                    activeTab === "execution" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"
                  }`}
                  onClick={() => setActiveTab("execution")}
                >
                  Execution Plan
                </button>
                <button
                  className={`justify-start text-left px-4 py-3 ${
                    activeTab === "unknowns" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"
                  }`}
                  onClick={() => setActiveTab("unknowns")}
                >
                  Key Unknowns
                </button>
                <button
                  className={`justify-start text-left px-4 py-3 ${
                    activeTab === "materials" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"
                  }`}
                  onClick={() => setActiveTab("materials")}
                >
                  Materials/Resources
                </button>
                <button
                  className={`justify-start text-left px-4 py-3 ${
                    activeTab === "tasks" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"
                  }`}
                  onClick={() => setActiveTab("tasks")}
                >
                  Task List
                </button>
                <button
                  className={`justify-start text-left px-4 py-3 ${
                    activeTab === "collaboration" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"
                  }`}
                  onClick={() => setActiveTab("collaboration")}
                >
                  Collaboration
                </button>
                <button
                  className={`justify-start text-left px-4 py-3 ${
                    activeTab === "stewardship" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"
                  }`}
                  onClick={() => setActiveTab("stewardship")}
                >
                  Stewardship
                </button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main content area */}
        <div className="md:col-span-3">
          {bitcoinEnabled && <ActionBtcWalletStatus actionId={action.id} />}

          <Card>
            <CardContent className="p-6">
              {activeTab === "objective" && <ObjectiveModule actionId={action.id} action={action} />}
              {activeTab === "budget" && <BudgetModule actionId={action.id} action={action} />}
              {activeTab === "timeline" && <TimelineModule actionId={action.id} action={action} />}
              {activeTab === "execution" && <ExecutionPlanModule actionId={action.id} action={action} />}
              {activeTab === "unknowns" && <KeyUnknownsModule actionId={action.id} action={action} />}
              {activeTab === "materials" && <MaterialsResourcesModule actionId={action.id} action={action} />}
              {activeTab === "tasks" && (
                <TaskListModule actionId={action.id} action={action} groupId={action.groupId} />
              )}
              {activeTab === "collaboration" && <CollaborationModule actionId={action.id} action={action} />}
              {stewardshipEnabled && activeTab === "stewardship" && <StewardshipModule actionId={action.id} action={action} />}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default ActionRefinementPage
